import moment from 'moment';
import { ComponentProps, useEffect, useState } from 'react';
import { TaskIdV2, UserId } from 'api/models';
import { getWeekStartDate } from 'components/organisms/timesheets/common/utils';
import { getDailyAllocatedMinutesFoProject } from 'components/organisms/timesheets/utils';
import { Maybe, Nothing } from 'core/maybe';
import { useUser } from 'hooks/use-user';
import { useAllocationMinutesForProjectForWeek } from 'queries/resource-management';
import { useTimesheetConfiguration } from 'queries/timesheets/configuration';
import { AddTimeEntryDrawerControlled } from './add-time-entry-drawer-controlled';
import { useSaveCancel } from './use-save-cancel';
import { useTimeSpent } from './use-time-spent';

interface Props {
  taskId: TaskIdV2;
  onClose: () => void;
  defaultBillable?: boolean;
  defaultCategory?: Maybe<number>;
  billableDisabled?: boolean;
  projectId: number;
}

export const useTimeEntryDrawerProps = ({
  taskId,
  onClose,
  defaultBillable = true,
  defaultCategory = Nothing(),
  billableDisabled = false,
  projectId,
}: Props): ComponentProps<typeof AddTimeEntryDrawerControlled> => {
  const [isAllowedDueToAllocation, setIsAllowedDueToAllocation] =
    useState(true);
  const today = moment().format('YYYY-MM-DD');
  const [entryDate, onDateChange] = useState<string>(today);
  const [minutes, onMinutesChange] = useState<Maybe<number>>(Nothing());
  const [category, onCategoryChange] = useState<Maybe<number>>(defaultCategory);
  const [billable, onBillableChange] = useState(defaultBillable);
  const [notes, onNotesChange] = useState<Maybe<string>>(Nothing());
  const {
    user: { userId },
  } = useUser();
  const { onSave, onCancel } = useSaveCancel({
    taskId,
    onClose,
    entryDate,
    minutes,
    category,
    billable,
    notes,
    userId: UserId(userId),
  });
  const { timeSpent } = useTimeSpent(taskId);
  const { data: configuration } = useTimesheetConfiguration();
  const { dailyMinutesForProject } = useAllocationMinutesForProjectForWeek();

  useEffect(() => {
    (async () => {
      if (configuration?.projectReportTimeSettings?.onDaysWithTimeAllocation) {
        const startDate = getWeekStartDate(entryDate).format('YYYY-MM-DD');

        const weekAllocation = await dailyMinutesForProject(
          [projectId],
          startDate,
          userId,
        );

        const dailyAllocationMinutesForProjects =
          getDailyAllocatedMinutesFoProject(weekAllocation, projectId);
        const isAllocationPresentForToday = dailyAllocationMinutesForProjects
          ? dailyAllocationMinutesForProjects[entryDate] > 0
          : true;

        setIsAllowedDueToAllocation(isAllocationPresentForToday);
      }
    })();
  }, [
    configuration?.projectReportTimeSettings?.onDaysWithTimeAllocation,
    projectId,
    dailyMinutesForProject,
    setIsAllowedDueToAllocation,
    userId,
    entryDate,
  ]);

  return {
    entryDate,
    onDateChange,
    minutes,
    onMinutesChange,
    billable,
    billableDisabled,
    onBillableChange,
    category,
    onCategoryChange,
    notes,
    onNotesChange,
    timeSpent,
    onSave,
    onCancel,
    isAllowedDueToAllocation,
  };
};
